import type { Register, VerificationType } from '@entities/Authentication/types.ts';
import { useSaveData } from '@entities/common/savedData';
import { useCallback } from 'react';

type EditorClientSharedState = Pick<
  Register,
  'password' | 'phonenumber' | 'phonenumber_code' | 'email_code' | 'email'
>;
type UpdateClientSharedStateData = Partial<EditorClientSharedState>;

export const useEditorClientSharedState = () => {
  const { doRemoveStorageData, doSetStorageData, data } = useSaveData<EditorClientSharedState>(
    'useEditorClientSharedState'
  );

  const doUpdateState = useCallback(
    (newData: UpdateClientSharedStateData) => doSetStorageData({ ...data, ...newData }),
    [doSetStorageData, data]
  );

  return {
    editorClientSharedState: data,
    updateEditorClientSharedState: doUpdateState,
    cleanEditorClientSharedState: doRemoveStorageData,
  };
};

type OpenVerifyT = {
  isModalOpen: boolean;
  onSuccessVerified: (props: { value?: string; passCode?: string }) => void;
  verification_type: VerificationType;
  value?: string;
};

export const useOpenVerify = (hashName: string) => {
  const { data, doRemoveStorageData, doSetStorageData } = useSaveData<OpenVerifyT>(hashName);

  const doOpenVerificationModal = useCallback(
    async ({
      onAction,
      ...props
    }: Omit<OpenVerifyT, 'isModalOpen'> & { onAction?: () => Promise<unknown> }) => {
      if (onAction) {
        try {
          await onAction();
          doSetStorageData({
            ...props,
            isModalOpen: true,
          });
        } catch (e) {}
      } else {
        doSetStorageData({
          ...props,
          isModalOpen: true,
        });
      }
    },
    [doSetStorageData]
  );

  const doCloseVerificationModal = useCallback(
    ({ value, passCode, isSuccess }: { isSuccess: boolean; value?: string; passCode?: string }) => {
      isSuccess && data?.onSuccessVerified && data.onSuccessVerified({ value, passCode });
      doRemoveStorageData();
    },
    [data, doRemoveStorageData]
  );

  return {
    state: data,
    doOpenVerificationModal,
    doCloseVerificationModal,
  };
};
